import React, { useState, useContext, useEffect } from 'react';
import { Form, Input, Button, notification, Card, Typography, Space, Divider, Alert } from 'antd';
import { NumericFormat } from 'react-number-format';
import { UserOutlined, DollarOutlined, CheckCircleOutlined, SearchOutlined } from '@ant-design/icons';
import axios from 'axios';
import './TransferFund.css';
import { AuthContext } from '../context/AuthContext';
import { getApiUrl } from '../utils/configHelper';

const { Title, Text } = Typography;

function TransferFund() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [foundUser, setFoundUser] = useState(null);
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [userBalance, setUserBalance] = useState(0);
  const [userCreditLimit, setUserCreditLimit] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const { user, isLoggedIn, updateUser } = useContext(AuthContext);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-US').format(amount);
  };

  // Fetch fresh user balance data on component mount
  useEffect(() => {
    const fetchUserBalance = async () => {
      if (user && user.whatsappNumber && user.active_accesskey) {
        setBalanceLoading(true);
        try {
          const apiUrl = `${getApiUrl()}/request/getUserData?active_accesskey=${user.active_accesskey}&phoneNo=${user.whatsappNumber}`;
          const response = await axios.get(apiUrl);
          if (response.data.status) {
            setUserBalance(response.data.data.accountBalance || 0);
            setUserCreditLimit(response.data.data.creditLimit || 0);
          } else {
            console.error('API returned status false:', response.data);
            setUserBalance(0);
            setUserCreditLimit(0);
          }
        } catch (error) {
          console.error('Error fetching user balance:', error);
          setUserBalance(0);
          setUserCreditLimit(0);
        } finally {
          setBalanceLoading(false);
        }
      }
    };

    fetchUserBalance();
  }, [user]);

  const searchUser = async () => {
    const phoneNumber = form.getFieldValue('phoneNumber');
    if (!phoneNumber) {
      notification.error({
        message: 'Error',
        description: 'Please enter a phone number',
        placement: 'topRight',
      });
      return;
    }

    // Check if user is trying to transfer to themselves
    if (phoneNumber === user?.whatsappNumber) {
      notification.error({
        message: 'Invalid Recipient',
        description: 'You cannot transfer funds to yourself',
        placement: 'topRight',
      });
      return;
    }

    setSearching(true);
    setFoundUser(null);
    setSelectedRecipient(null);

    try {
      const apiUrl = `${getApiUrl()}/request/findUserByPhone`;
      const response = await axios.post(apiUrl, { phoneNo: phoneNumber });

      if (response.data.status) {
        setFoundUser({
          ...response.data.data,
          fullName: `${response.data.data.firstName} ${response.data.data.lastName}`.trim()
        });
      } else {
        notification.error({
          message: 'User Not Found',
          description: response.data.message || 'No user found with this phone number',
          placement: 'topRight',
        });
      }
    } catch (error) {
      console.error('Search user error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to search user';
      notification.error({
        message: 'Error',
        description: errorMessage,
        placement: 'topRight',
      });
    } finally {
      setSearching(false);
    }
  };

  const selectUser = () => {
    if (foundUser) {
      setSelectedRecipient(foundUser);
    }
  };

  const resetTransfer = () => {
    setFoundUser(null);
    setSelectedRecipient(null);
    form.resetFields(['amount']);
  };

  const onFinish = async (values) => {
    if (!selectedRecipient) {
      notification.error({
        message: 'Error',
        description: 'Please search and select a recipient first',
        placement: 'topRight',
      });
      return;
    }

    const transferAmount = parseFloat(values.amount.replace(/,/g, ''));
    const availableBalance = userBalance + userCreditLimit;

    if (transferAmount > availableBalance) {
      notification.error({
        message: 'Insufficient Balance',
        description: `You have ${formatAmount(availableBalance)} SSP available (${formatAmount(userBalance)} SSP balance + ${formatAmount(userCreditLimit)} SSP credit)`,
        placement: 'topRight',
      });
      return;
    }

    setLoading(true);
    const { active_accesskey, whatsappNumber } = user;

    console.log('Transfer payload:', {
      active_accesskey,
      user_id: whatsappNumber,
      recipientPhone: selectedRecipient.phoneNumber,
      amount: transferAmount,
    });

    const payload = {
      active_accesskey: active_accesskey,
      user_id: whatsappNumber,
      recipientPhone: selectedRecipient.phoneNumber,
      amount: transferAmount,
    };

    try {
      const apiUrl = `${getApiUrl()}/request/transferFunds`;
      const response = await axios.post(apiUrl, payload);

      if (response.data.status) {
        notification.success({
          message: 'Transfer Successful',
          description: `Successfully transferred ${formatAmount(transferAmount)} SSP to ${selectedRecipient.fullName}`,
          placement: 'topRight',
        });

        // Refetch fresh balance data from server
        try {
          const apiUrl = `${getApiUrl()}/request/getUserData?active_accesskey=${user.active_accesskey}&phoneNo=${user.whatsappNumber}`;
          const balanceResponse = await axios.get(apiUrl);
          if (balanceResponse.data.status) {
            setUserBalance(balanceResponse.data.data.accountBalance || 0);
            setUserCreditLimit(balanceResponse.data.data.creditLimit || 0);
          }
        } catch (error) {
          console.error('Error refetching balance:', error);
          // If refetch fails, set loading to true to indicate balance may be stale
          setBalanceLoading(true);
        }
        // Reset form
        form.resetFields();
        setFoundUser(null);
        setSelectedRecipient(null);
      } else {
        notification.error({
          message: 'Transfer Failed',
          description: response.data.message || 'Failed to transfer funds',
          placement: 'topRight',
        });
      }
    } catch (error) {
      console.error('Transfer error:', error);
      const errorData = error.response?.data;
      let errorMessage = 'Failed to transfer funds';

      if (errorData) {
        errorMessage = errorData.message || errorMessage;

        // Handle authentication errors
        if (errorData.error === 'INVALID_ACCESS_KEY' || errorData.error === 'USER_NOT_FOUND') {
          errorMessage = 'Your session has expired. Please login again.';
          // Optionally clear user data and redirect
          // logout(); // if we had access to logout function
        }
      }

      notification.error({
        message: 'Transfer Failed',
        description: errorMessage,
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  };

  const onFinishFailed = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  // Check if user is logged in
  if (!isLoggedIn || !user) {
    return (
      <div className="transfer-fund-container">
        <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Title level={3}>Login Required</Title>
          <Text type="secondary">
            You need to be logged in to transfer funds. Please login to continue.
          </Text>
        </Card>
      </div>
    );
  }

  return (
    <div className="transfer-fund-container">
      <div className="transfer-header">
        <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
          <DollarOutlined style={{ marginRight: 8 }} />
          Transfer Funds
        </Title>
        <Text type="secondary">Send money to other users instantly</Text>
      </div>

      {/* Balance Display */}
      <Card className="balance-card" size="small">
        <Space direction="vertical" size="small">
          <Text strong>Your Available Balance</Text>
          {balanceLoading ? (
            <div>Loading balance...</div>
          ) : (
            <>
              <Title level={3} style={{ margin: 0, color: '#52c41a' }}>
                {formatAmount(userBalance + userCreditLimit)} SSP
              </Title>
              <Text type="secondary">
                Balance: {formatAmount(userBalance)} SSP | Credit Limit: {formatAmount(userCreditLimit)} SSP
              </Text>
            </>
          )}
        </Space>
      </Card>

      <Card className="transfer-card">
        <Form
          form={form}
          name="transferFundForm"
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          layout="vertical"
        >
          {/* Step 1: Phone Number Search */}
          <div className="transfer-step">
            <Title level={4}>
              <UserOutlined style={{ marginRight: 8 }} />
              Step 1: Find Recipient
            </Title>

            <Form.Item
              name="phoneNumber"
              rules={[
                {
                  required: true,
                  message: 'Please input phone number!',
                },
                {
                  pattern: /^[+]?\d+$/,
                  message: 'Please enter a valid phone number!',
                },
              ]}
            >
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  placeholder="Enter recipient's phone number"
                  type="number"
                  size="large"
                  style={{ flex: 1 }}
                  disabled={selectedRecipient !== null}
                />
                {!selectedRecipient && (
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={searchUser}
                    loading={searching}
                    disabled={searching}
                    size="large"
                  >
                    {searching ? 'Searching...' : 'Search'}
                  </Button>
                )}
              </Space.Compact>
            </Form.Item>

            {/* Found User Display */}
            {foundUser && !selectedRecipient && (
              <Card
                className="user-found-card"
                size="small"
                hoverable
                onClick={selectUser}
                style={{ cursor: 'pointer' }}
              >
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
                    <Text strong>User Found - Click to Select</Text>
                  </div>
                  <div>
                    <Text style={{ fontSize: '16px', fontWeight: 'bold' }}>{foundUser.fullName}</Text>
                    <br />
                    <Text type="secondary">{foundUser.phoneNumber}</Text>
                  </div>
                </Space>
              </Card>
            )}

            {/* Selected Recipient Display */}
            {selectedRecipient && (
              <Card className="recipient-selected-card" size="small">
                <Space direction="vertical" size="small">
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <CheckCircleOutlined style={{ color: '#1890ff', marginRight: 8 }} />
                    <Text strong>Recipient Selected</Text>
                  </div>
                  <div>
                    <Text style={{ fontSize: '16px', fontWeight: 'bold' }}>{selectedRecipient.fullName}</Text>
                    <br />
                    <Text type="secondary">{selectedRecipient.phoneNumber}</Text>
                  </div>
                  <Button onClick={resetTransfer} size="small" danger>
                    Change Recipient
                  </Button>
                </Space>
              </Card>
            )}
          </div>

          {/* Step 2: Amount Input - Only show when recipient is selected */}
          {selectedRecipient && (
            <>
              <Divider />
              <div className="transfer-step">
                <Title level={4}>
                  <DollarOutlined style={{ marginRight: 8 }} />
                  Step 2: Enter Amount
                </Title>

                <Form.Item
                  name="amount"
                  rules={[
                    {
                      required: true,
                      message: 'Please input amount!',
                    },
                    {
                      validator: (_, value) => {
                        if (value) {
                          const numValue = parseFloat(value.replace(/,/g, ''));
                          const availableBalance = userBalance + userCreditLimit;
                          if (numValue > availableBalance) {
                            return Promise.reject(`Amount exceeds available balance of ${formatAmount(availableBalance)} SSP`);
                          }
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <NumericFormat
                    placeholder="Enter amount to transfer"
                    thousandSeparator=","
                    decimalScale={2}
                    customInput={Input}
                    size="large"
                    suffix=" SSP"
                  />
                </Form.Item>

                {/* Transfer Summary */}
                <Alert
                  message="Transfer Summary"
                  description={
                    <div>
                      <Text>From: <strong>You</strong></Text><br />
                      <Text>To: <strong>{selectedRecipient.fullName}</strong></Text><br />
                      <Text>Amount: <strong>{form.getFieldValue('amount') ? formatAmount(parseFloat(form.getFieldValue('amount').replace(/,/g, ''))) : '0'} SSP</strong></Text>
                    </div>
                  }
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />

                {/* Submit Button */}
                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    disabled={loading}
                    size="large"
                    block
                    style={{ height: '48px', fontSize: '16px' }}
                  >
                    {loading ? 'Processing Transfer...' : 'Transfer Funds'}
                  </Button>
                </Form.Item>
              </div>
            </>
          )}
        </Form>
      </Card>
    </div>
  );
}

export default TransferFund;
